import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiBaseService } from '../api-base-service';
import { Success } from '../responses/api-response';
import { StorageService } from '@portal/shared/services/storage.service';
import {
    AdjustOnHandCommandDto,
    CreateInventoryItemDto,
    InventoryBalanceDto,
    InventoryBalanceQuery,
    InventoryCatalogQuery,
    InventoryItemDto,
    InventoryItemType,
    InventoryMovementDto,
    InventoryMovementQuery,
    PartyEventInventoryBalanceDto,
    PartyEventInventoryMutationResultDto,
    PartyEventInventoryStateDto,
    PartyEventInventoryQuantityCommandDto,
    PagedResultDto,
    ReceiveStockCommandDto,
    UpdateInventoryItemDto
} from './inventory.models';

@Injectable({
    providedIn: 'root'
})
export class InventoryApiService extends ApiBaseService {
    constructor(
        public http: HttpClient,
        private readonly _storageService: StorageService
    ) {
        super(http);
    }

    getCatalog(query: InventoryCatalogQuery): Observable<Success<PagedResultDto<InventoryItemDto>>> {
        const scoped = this.withOrganizationScope(query);
        const requestQuery = {
            ...scoped,
            itemType: scoped.type ? this.toApiItemType(scoped.type) : undefined,
            type: undefined
        };

        return this.get<Success<PagedResultDto<InventoryItemDto>>>(`inventory/catalog?${this.buildQuery(requestQuery)}`);
    }

    createCatalogItem(payload: CreateInventoryItemDto): Observable<Success<InventoryItemDto>> {
        const requestPayload = this.withOrganizationScope({
            ...payload,
            itemType: this.toApiItemType(payload.itemType)
        });

        return this.post<Success<InventoryItemDto>, any>(requestPayload, 'inventory/items').pipe(
            catchError((error) => {
                if (error?.status === 404 || error?.status === 405) {
                    return this.post<Success<InventoryItemDto>, any>(requestPayload, 'inventory/catalog');
                }
                return throwError(() => error);
            })
        );
    }

    updateCatalogItem(itemId: string, payload: UpdateInventoryItemDto): Observable<Success<InventoryItemDto>> {
        const requestPayload = this.withOrganizationScope({
            ...payload,
            itemType: this.toApiItemType(payload.itemType)
        });

        return this.put<Success<InventoryItemDto>, any>(requestPayload, `inventory/items/${itemId}`);
    }

    deleteCatalogItem(itemId: string, organizationId?: string): Observable<Success<boolean>> {
        const query = this.buildQuery(this.withOrganizationScope({ organizationId }));
        const querySuffix = query ? `?${query}` : '';
        return this.deleteBase<Success<boolean>>(`inventory/items/${itemId}${querySuffix}`);
    }

    getBalances(query: InventoryBalanceQuery): Observable<Success<PagedResultDto<InventoryBalanceDto>>> {
        return this.get<Success<PagedResultDto<InventoryBalanceDto>>>(`inventory/balances?${this.buildQuery(this.withOrganizationScope(query))}`);
    }

    getMovements(query: InventoryMovementQuery): Observable<Success<PagedResultDto<InventoryMovementDto>>> {
        return this.get<Success<PagedResultDto<InventoryMovementDto>>>(`inventory/movements?${this.buildQuery(this.withOrganizationScope(query))}`);
    }

    getPartyEventBalances(
        partyEventId: string,
        query: InventoryBalanceQuery
    ): Observable<Success<PagedResultDto<PartyEventInventoryBalanceDto>>> {
        return this.get<Success<PagedResultDto<PartyEventInventoryBalanceDto>>>(
            `party-events/${partyEventId}/inventory/balances?${this.buildQuery(this.withOrganizationScope(query))}`
        );
    }

    getPartyEventState(
        partyEventId: string,
        organizationId?: string
    ): Observable<Success<PartyEventInventoryStateDto>> {
        return this.get<Success<PartyEventInventoryStateDto>>(
            `party-events/${partyEventId}/inventory/state?${this.buildQuery(this.withOrganizationScope({ organizationId }))}`
        );
    }

    receive(payload: ReceiveStockCommandDto): Observable<Success<InventoryBalanceDto>> {
        return this.post<Success<InventoryBalanceDto>, ReceiveStockCommandDto>(this.withOrganizationScope(payload), 'inventory/receive');
    }

    adjust(payload: AdjustOnHandCommandDto): Observable<Success<InventoryBalanceDto>> {
        return this.post<Success<InventoryBalanceDto>, AdjustOnHandCommandDto>(this.withOrganizationScope(payload), 'inventory/adjust');
    }

    allocate(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/allocate`
        );
    }

    consume(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/consume`
        );
    }

    returnFromEvent(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/return`
        );
    }

    loss(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/loss`
        );
    }

    reconcile(
        partyEventId: string,
        payload?: { reason?: string; organizationId?: string }
    ): Observable<Success<PartyEventInventoryMutationResultDto[]>> {
        return this.post<Success<PartyEventInventoryMutationResultDto[]>, { reason?: string; organizationId?: string }>(
            this.withOrganizationScope(payload ?? {}),
            `party-events/${partyEventId}/inventory/reconcile`
        );
    }

    reopenPartyEventInventory(
        partyEventId: string,
        organizationId?: string
    ): Observable<Success<PartyEventInventoryStateDto>> {
        return this.post<Success<PartyEventInventoryStateDto>, { organizationId?: string }>(
            this.withOrganizationScope({ organizationId }),
            `party-events/${partyEventId}/inventory/reopen`
        );
    }

    private buildQuery<T extends object>(params: T): string {
        const entries = Object.entries(params as Record<string, unknown>)
            .filter(([, value]) => value !== undefined && value !== null && value !== '');

        return entries
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join('&');
    }

    private withOrganizationScope<T extends { organizationId?: string }>(payload: T): T {
        const organizationId =
            this.coerceToString(payload.organizationId) ||
            this.coerceToString(this._storageService.getOrg()?.id as unknown) ||
            this.tryGetOrgIdFromCurrentUrl();
        if (!organizationId) {
            return payload;
        }

        return {
            ...payload,
            organizationId
        };
    }

    private coerceToString(value: unknown): string | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        const asString = String(value).trim();
        return asString.length > 0 ? asString : undefined;
    }

    private tryGetOrgIdFromCurrentUrl(): string | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const match = window.location.pathname.match(/\/org\/([0-9a-fA-F-]{36})(?:\/|$)/i);
        return match?.[1];
    }

    private toApiItemType(itemType: InventoryItemType | number | string): number {
        if (typeof itemType === 'number') {
            return itemType;
        }

        const map: Record<string, number> = {
            Asset: 1,
            Consumable: 2,
            Merchandise: 3
        };

        const mapped = map[String(itemType)];
        if (mapped !== undefined) {
            return mapped;
        }

        const asNumber = Number(itemType);
        return Number.isFinite(asNumber) ? asNumber : 0;
    }
}
